import React from 'react';
import type { BasicFilters } from '../../components/FilterPanel';
import type { VettingStatus } from '../../types/vetting';
import type { ProgramSearchResults, SearchFilters } from '../../types/rpc-contracts';
import { createProgramPrintJob } from '../../services/rpc/printJobs';
import { useHashRoute } from '../../hooks/useHashRoute';
import { buildProgramSearchFilters, buildPrintFilters } from './filter-mapping';
import { useProgramSearch } from './use-program-search';

const DEFAULT_VETTING_SELECTION: VettingStatus[] = ['green', 'yellow'];

const DEFAULT_FILTERS: BasicFilters = {
  sort_by: 'categories',
  sort_direction: 'desc',
  // Additional filters with default values
  entry_fees: 'all',
  vetting_statuses: DEFAULT_VETTING_SELECTION,
  isbn_required: 'all',
  independent_publishers_allowed: 'all',
  multiple_prizes_offered: 'all'
};

const ALLOWED_SORTS = new Set<BasicFilters['sort_by']>(['name', 'categories']);
const ALLOWED_PAGE_SIZES = [10, 25, 50, 100];

export interface DataQualityController {
  searchTerm: string;
  committedSearch: string;
  setSearchTerm: (value: string) => void;
  applySearch: () => void;
  clearSearch: () => void;
  filters: BasicFilters;
  updateFilters: (next: BasicFilters) => void;
  page: number;
  setPage: (value: number) => void;
  pageSize: number;
  setPageSize: (value: number) => void;
  selectedProgramIds: Set<string>;
  setSelectedProgramIds: (value: Set<string>) => void;
  selectedSearchId: string | null;
  setSelectedSearchId: (value: string | null) => void;
  query: {
    data: ProgramSearchResults | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => Promise<ProgramSearchResults | undefined>;
  };
  searchFilters: SearchFilters;
  printFilters: SearchFilters;
  startPrintJob: (selectedIds: Set<string>) => Promise<string>;
  activeCategoryFilter: {
    categoryIds: string[];
    readerSegment: string | null;
    tier1: string | null;
    tier2: string | null;
  } | null;
  clearCategoryFilter: () => void;
}

export function useDataQualityController(): DataQualityController {
  const { view, params, setParams } = useHashRoute('data-quality');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [committedSearch, setCommittedSearch] = React.useState('');
  const [filters, setFilters] = React.useState<BasicFilters>(() => ({ ...DEFAULT_FILTERS }));
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [selectedProgramIds, setSelectedProgramIds] = React.useState<Set<string>>(new Set());
  const [selectedSearchId, setSelectedSearchId] = React.useState<string | null>(null);
  
  // Use ref to track previous params to avoid circular dependency
  const prevParamsRef = React.useRef<Record<string, any>>({});

  const categoryIds = React.useMemo(() => {
    if (view !== 'data-quality') {
      return undefined;
    }
    const raw = params.category_ids;
    if (!raw) {
      return undefined;
    }
    return raw
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }, [params.category_ids, view]);

  const readerSegment = view === 'data-quality' && params.reader_segment ? params.reader_segment : null;
  const tier1 = view === 'data-quality' && params.tier1 ? params.tier1 : null;
  const tier2 = view === 'data-quality' && params.tier2 ? params.tier2 : null;

  const activeCategoryFilter = React.useMemo(() => {
    if (!categoryIds || categoryIds.length === 0) {
      return null;
    }

    return {
      categoryIds,
      readerSegment,
      tier1,
      tier2,
    } as const;
  }, [categoryIds, readerSegment, tier1, tier2]);

  // Keep local state in sync with route params (includes back/forward navigation)
  React.useEffect(() => {
    if (view !== 'data-quality') {
      return;
    }

    const qParam = params.q ?? '';
    setSearchTerm((current) => (current === qParam ? current : qParam));
    setCommittedSearch((current) => (current === qParam ? current : qParam));

    const sbParam = params.sb;
    const nextSortBy = sbParam && ALLOWED_SORTS.has(sbParam as BasicFilters['sort_by'])
      ? (sbParam as BasicFilters['sort_by'])
      : DEFAULT_FILTERS.sort_by;

    const sdParam = params.sd === 'desc'
      ? 'desc'
      : params.sd === 'asc'
        ? 'asc'
        : DEFAULT_FILTERS.sort_direction;

    setFilters((current) => {
      let changed = false;
      const next: BasicFilters = { ...current };

      if (next.sort_by !== nextSortBy) {
        next.sort_by = nextSortBy;
        changed = true;
      }

      if (next.sort_direction !== sdParam) {
        next.sort_direction = sdParam;
        changed = true;
      }

      return changed ? next : current;
    });

    const pParam = params.p ? Math.max(1, parseInt(params.p, 10) || 1) : 1;
    const nextPage = pParam - 1;
    setPage((current) => (current === nextPage ? current : nextPage));

    const psParamRaw = params.ps ? parseInt(params.ps, 10) : undefined;
    const nextPageSize = psParamRaw && ALLOWED_PAGE_SIZES.includes(psParamRaw)
      ? psParamRaw
      : 50;
    setPageSize((current) => (current === nextPageSize ? current : nextPageSize));

  }, [params, view]);

  // Persist filters/search into URL hash whenever the relevant state changes
  // Guard against infinite loops by only updating the URL when values actually change
  React.useEffect(() => {
    if (view !== 'data-quality') {
      return;
    }

    const nextParams: Record<string, string | number | boolean | null | undefined> = {
      q: committedSearch || undefined,
      sb: filters.sort_by,
      sd: filters.sort_direction,
      p: page > 0 ? page + 1 : undefined,
      ps: pageSize !== 50 ? pageSize : undefined,
    };

    if (categoryIds && categoryIds.length > 0) {
      nextParams.category_ids = categoryIds.join(',');
    }

    if (readerSegment) {
      nextParams.reader_segment = readerSegment;
    }

    if (tier1) {
      nextParams.tier1 = tier1;
    }

    if (tier2) {
      nextParams.tier2 = tier2;
    }

    // Normalize current and next params to comparable shapes (omit defaults)
    const normalize = (obj: Record<string, any>) => {
      const out: Record<string, string | number | boolean | null | undefined> = {};
      if (obj.q) out.q = String(obj.q);
      // Always include sort_by and sort_direction to avoid inconsistencies
      out.sb = obj.sb || DEFAULT_FILTERS.sort_by;
      out.sd = obj.sd === 'desc' ? 'desc' : 'asc';
      const pNum = obj.p ? Math.max(1, parseInt(String(obj.p), 10) || 1) : undefined;
      if (pNum && pNum > 1) out.p = pNum;
      const psNum = obj.ps ? parseInt(String(obj.ps), 10) : undefined;
      if (psNum && ALLOWED_PAGE_SIZES.includes(psNum) && psNum !== 50) out.ps = psNum;
      if (obj.category_ids) out.category_ids = String(obj.category_ids);
      if (obj.reader_segment) out.reader_segment = String(obj.reader_segment);
      if (obj.tier1) out.tier1 = String(obj.tier1);
      if (obj.tier2) out.tier2 = String(obj.tier2);
      return out;
    };

    const shallowEqual = (a: Record<string, any>, b: Record<string, any>) => {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      for (const k of aKeys) {
        if (a[k] !== b[k]) return false;
      }
      return true;
    };

    const currentParamsNormalized = normalize(prevParamsRef.current);
    const nextParamsNormalized = normalize(nextParams);

    if (!shallowEqual(currentParamsNormalized, nextParamsNormalized)) {
      prevParamsRef.current = nextParams as any;
      setParams(nextParams, { replaceAll: true });
    }
  }, [categoryIds, committedSearch, filters.sort_by, filters.sort_direction, page, pageSize, readerSegment, setParams, tier1, tier2, view]);
  
  // Update ref when params change from external sources (like browser back/forward)
  React.useEffect(() => {
    if (view === 'data-quality') {
      prevParamsRef.current = params as any;
    }
  }, [params, view]);

  // Debounce typing into committed search term
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      setCommittedSearch(searchTerm.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  const searchFilters = React.useMemo(() => {
    return buildProgramSearchFilters({
      filters,
      searchTerm: committedSearch,
      page,
      pageSize,
      categoryIds,
      readerSegment,
      tier1,
      tier2
    });
  }, [categoryIds, filters, committedSearch, page, pageSize, readerSegment, tier1, tier2]);

  const queryResult = useProgramSearch({ filters: searchFilters });

  const applySearch = React.useCallback(() => {
    setCommittedSearch(searchTerm.trim());
    setPage(0);
  }, [searchTerm]);

  const clearSearch = React.useCallback(() => {
    setSearchTerm('');
    setCommittedSearch('');
    setPage(0);
  }, []);

  const updateFilters = React.useCallback((next: BasicFilters) => {
    setFilters((current) => {
      // Shallow compare to avoid redundant state updates
      const keys = new Set([...Object.keys(current || {}), ...Object.keys(next || {})]);
      for (const k of keys) {
        if ((current as any)[k] !== (next as any)[k]) {
          // Filters changed; reset pagination and apply
          setPage(0);
          return next;
        }
      }
      // No changes; keep state and do not reset page
      return current;
    });
  }, []);

  const printFilters = React.useMemo(() => buildPrintFilters(searchFilters), [searchFilters]);

  const startPrintJob = React.useCallback(async (selectedIds: Set<string>) => {
    const selected = Array.from(selectedIds);
    return await createProgramPrintJob(printFilters as Record<string, unknown>, selected.length ? selected : null);
  }, [printFilters]);

  const clearCategoryFilter = React.useCallback(() => {
    if (view !== 'data-quality') {
      return;
    }

    const nextParams: Record<string, string | number | boolean | null | undefined> = {
      q: committedSearch || undefined,
      sb: filters.sort_by,
      sd: filters.sort_direction,
      p: page > 0 ? page + 1 : undefined,
      ps: pageSize !== 50 ? pageSize : undefined,
    };

    setPage(0);
    prevParamsRef.current = nextParams as any;
    setParams(nextParams, { replaceAll: true });
  }, [committedSearch, filters.sort_by, filters.sort_direction, page, pageSize, setParams, setPage, view]);

  return {
    searchTerm,
    committedSearch,
    setSearchTerm,
    applySearch,
    clearSearch,
    filters,
    updateFilters,
    page,
    setPage,
    pageSize,
    setPageSize: (value: number) => {
      setPageSize(value);
      setPage(0);
    },
    selectedProgramIds,
    setSelectedProgramIds,
    selectedSearchId,
    setSelectedSearchId,
    query: {
      data: queryResult.data,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
      error: queryResult.error,
      refetch: async () => {
        const result = await queryResult.refetch();
        return result.data;
      }
    },
    searchFilters,
    printFilters,
    startPrintJob,
    activeCategoryFilter,
    clearCategoryFilter
  };
}
